/**
 * SharedUsersList.tsx
 * 
 * This component displays a list of users with whom a posit has been shared.
 * It includes functionality to remove access for shared users.
 * 
 * Changes:
 * - Extracted from ShareDialog.tsx to improve component modularity
 */

import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from "sonner";
import { supabase } from '@/integrations/supabase/client';

interface SharedUser {
  id: string;
  email: string;
}

interface SharedUsersListProps {
  positId: string;
  sharedUsers: SharedUser[];
  onRemoveAccess: () => void;
}

export function SharedUsersList({ positId, sharedUsers, onRemoveAccess }: SharedUsersListProps) {
  const handleRemoveAccess = async (email: string) => {
    try {
      const { error } = await supabase.functions.invoke('remove-posit-access', {
        body: {
          positId,
          email,
        },
      });

      if (error) {
        toast.error("Failed to remove access");
        return;
      }

      toast.success(`Access removed for ${email}`);
      onRemoveAccess(); // Refresh the list
    } catch (error) {
      toast.error("Failed to remove access");
    }
  };

  if (sharedUsers.length === 0) {
    return null;
  }

  return (
    <div className="mt-6">
      <p className="text-sm font-medium mb-2">Shared with:</p>
      <ul className="space-y-2">
        {sharedUsers.map((user) => (
          <li key={user.id} className="flex items-center justify-between text-sm p-2 bg-muted rounded-md">
            <span>{user.email}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleRemoveAccess(user.email)}
              className="h-6 w-6"
            >
              <X className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
} 